import React, { useEffect } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  ShippingCart,
  ShippingCartItem,
  ShippingCartStatus,
  ShippingCartType,
  ShippingCartViewProps,
} from "./ShippingCart.types";
import {
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import { Grid } from "@mui/material";
import { ShippingCartItemView } from "./ShippingCartItemView";
import { ValidationErrors, ValidationErrorsEntity } from "packable-common";

const useStyles = (theme: Theme) => ({
  PackableShippingCartViewContaner: {
    pl: 1,
    pr: 1,
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
  },
});

export const ShippingCartView: React.FC<ShippingCartViewProps> = (
  props
): React.JSX.Element => {
  const {
    userAppState,
    handleCloseShippingCart,
    shippingCart,
    onShippingCartUpdate,
    validationErrorsList,
    onValidationErrorsListUpdate,
    styles,
  } = props;

  logDebug("ShippingCartView PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("ShippingCartView effectiveStyles");
  logDebug(effectiveStyles);

  const getValidationErrors = (itemId: string): ValidationErrors => {
    let validationErrors: ValidationErrors | undefined =
      validationErrorsList.find((ve) => ve.entityId === itemId);
    if (validationErrors === undefined) {
      validationErrors = new ValidationErrorsEntity(itemId);
      validationErrorsList.push(validationErrors);
    }
    return validationErrors;
  };

  const resetValidationErrors = () => {
    const updatedList = validationErrorsList.filter(
      (ve) =>
        ve.entityId ===
          shippingCart.cartItems?.find((ci) => ci.itemId === ve.entityId)
            ?.itemId || ""
    );
    onValidationErrorsListUpdate(updatedList);
  };

  const onValidationErrorsUpdate = (
    validationErrors: ValidationErrors
  ): void => {
    let updatedValidationErrorsList: Array<ValidationErrors> =
      packableDeepClone(validationErrorsList);
    const index = validationErrorsList.findIndex(
      (vel) => vel.entityId === validationErrors.entityId
    );
    if (index !== -1) {
      updatedValidationErrorsList[index] = validationErrors;
    } else {
      updatedValidationErrorsList.push(validationErrors);
    }
    onValidationErrorsListUpdate(updatedValidationErrorsList);
  };

  const onShippingCartItemUpdate = (
    shippingCartItem: ShippingCartItem
  ): void => {
    if (shippingCart.cartItems !== null) {
      const index = shippingCart.cartItems?.findIndex(
        (item) => item.itemId === shippingCartItem.itemId
      );
      if (index !== -1) {
        let updatedCart: ShippingCart = packableDeepClone(shippingCart);
        if (updatedCart.cartItems) {
          updatedCart.cartItems[index] = shippingCartItem;
          if (shippingCartItem.useTheSameDateAndTime) {
            updatedCart.cartItems.forEach((item) => {
              if (item.useTheSameDateAndTime) {
                item.storageEndDate = shippingCartItem.storageEndDate;
              }
            });
          }
        }
        onShippingCartUpdate(updatedCart);
      }
    }
  };

  const onRemoveShippingCartItem = (itemId: string): void => {
    if (shippingCart.cartItems !== null) {
      // remove all validation errors for this item
      const errorListIndex = validationErrorsList.findIndex(
        (ve) => ve.entityId === itemId
      );
      if (errorListIndex !== -1) {
        validationErrorsList[errorListIndex].errors = [];
      }
      onValidationErrorsListUpdate(validationErrorsList);
      // and remove the item
      const updatedItems = shippingCart.cartItems.filter(
        (item) => item.itemId !== itemId
      );
      let updatedCart: ShippingCart = packableDeepClone(shippingCart);
      updatedCart.cartItems = updatedItems;
      if (updatedCart.cartItems.length === 0) {
        updatedCart.cartStatus = ShippingCartStatus.INACTIVE;
        updatedCart.cartType = ShippingCartType.STORAGE;
      }
      onShippingCartUpdate(updatedCart);
      if (updatedCart.cartItems.length === 0) {
        handleCloseShippingCart();
      }
    }
  };

  const multipleItmes = (): boolean => {
    let rc = false;
    if (shippingCart.cartItems && shippingCart.cartItems.length > 1) {
      rc = true;
    }
    return rc;
  };

  useEffect(() => {
    resetValidationErrors();
  }, []);

  return (
    <Grid
      className="PackableShippingCartViewContaner"
      sx={effectiveStyles.PackableShippingCartViewContaner}
    >
      {shippingCart.cartItems
        ?.sort(
          (a, b) =>
            Number(a.itemLabel.substring(3)) - Number(b.itemLabel.substring(3))
        )
        .map((item) => (
          <ShippingCartItemView
            shippingCartType={shippingCart.cartType}
            shippingCartItem={item}
            multipleItems={multipleItmes()}
            onShippingCartItemUpdate={onShippingCartItemUpdate}
            onRemoveShippingCartItem={onRemoveShippingCartItem}
            validationErrors={getValidationErrors(item.itemId)}
            onValidationErrorsUpdate={onValidationErrorsUpdate}
            userAppState={userAppState}
            styles={effectiveStyles}
          />
        ))}
    </Grid>
  );
};
